import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import {func, object} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {canChangePublicationStatus} from '../../auth.js';
import {
  useDataTypes,
  useKeywords,
  usePermissions,
  usePublicationTypes,
  useTopics,
  useUploadAttachment,
} from '../../client.js';
import AuthorGroup from './AuthorGroup.jsx';
import ComboBox from './ComboBox.jsx';
import Form from './Form.jsx';
import GeometryInput from './GeometryInput.jsx';
import InputList from './InputList.jsx';
import LinkGroup from './LinkGroup.jsx';
import MultiSelect from './MultiSelect.jsx';
import TextField, {utcDateTransform} from './TextField.jsx';
import Toggle from './Toggle.jsx';

function historicalDateValidator(value) {
  return Date.parse(value) < Date.now() || 'Date must be in the past';
}

/**
 * Some detail about publication links.
 *
 * There are three link rel values that we care about:
 *
 *  - alternate: publicly accessible URL for the publication on the internet (one)
 *  - attachment: links to an internally stored PDF copy of the publication (zero or one)
 *  - related: links to blog post, etc. that may be related to the publication (zero or more)
 *
 * The API will also return other links that we don't care about (e.g. self).  The order of the
 * links from the API is also not guaranteed.  When we create or edit a publication, we only include
 * the links that we care about, and the form input elements have a fixed order (this is important):
 *
 *  - 0: alternate
 *  - 1: attachment (even if it is empty)
 *  - 2..n: related
 */

function PublicationForm({onSubmit, publication}) {
  const {data: keywords} = useKeywords();
  const {data: topics} = useTopics();
  const {data: dataTypes} = useDataTypes();
  const {data: publicationTypes} = usePublicationTypes();
  const [file, setFile] = useState();
  const [showGeometry, setShowGeometry] = useState(!!publication?.geometry);
  const {
    request: uploadAttachment,
    data: uploadedAttachment,
    error: uploadError,
    loading: uploadingAttachment,
  } = useUploadAttachment();
  const {data: permissions} = usePermissions();

  const alternateLinks = [];
  const attachmentLinks = [];
  const relatedLinks = [];
  if (publication) {
    for (const link of publication.links) {
      if (link.rel === 'alternate') {
        alternateLinks.push(link);
      } else if (link.rel === 'attachment') {
        attachmentLinks.push(link);
      } else if (link.rel === 'related') {
        relatedLinks.push(link);
      }
    }
  }

  const [attachmentLink, setAttachmentLink] = useState(
    attachmentLinks[0] || {},
  );

  const relatedLinkOffset = 2;

  const handleFileChange = useCallback(
    event => {
      const attachment = event.target.files[0];
      if (attachment && attachment.type === 'application/pdf') {
        setFile(attachment);
        uploadAttachment(attachment);
      }
    },
    [uploadAttachment],
  );

  const handleGeometryAccordionToggle = useCallback(
    () => setShowGeometry(showGeometry => !showGeometry),
    [],
  );

  useEffect(() => {
    if (!file || !uploadedAttachment) {
      return;
    }
    setAttachmentLink({
      title: file.name,
      type: file.type,
      rel: 'attachment',
      href: uploadedAttachment.id,
    });
  }, [file, uploadedAttachment]);

  let attachmentStatus;
  if (!file) {
    attachmentStatus = attachmentLink.href
      ? 'Replace attachment'
      : 'Upload attachment (PDF)';
  } else if (uploadingAttachment) {
    attachmentStatus = `Uploading ${file.name}`;
  } else if (uploadError) {
    attachmentStatus = `Error uploading ${file.name}: ${uploadError.message}`;
  } else {
    attachmentStatus = `Attached ${file.name}`;
  }

  return (
    <Form onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            name="title"
            label="Title"
            defaultValue={publication?.title}
            required
            fullWidth
            autoFocus
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <LinkGroup
            label="Link to publication"
            required
            requireTitle={false}
            rel="alternate"
            value={alternateLinks[0] || null}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            fullWidth
            name="publisher"
            label="Journal/Publication"
            defaultValue={publication?.publisher}
            required
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <TextField
            fullWidth
            name="datePublished"
            label="Date Published"
            defaultValue={publication?.datePublished}
            required
            type="date"
            transform={utcDateTransform}
            rules={{validate: historicalDateValidator}}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <InputList
            offset={0}
            list={publication?.authors}
            component={<AuthorGroup />}
            label="Author"
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <TextField
            name="abstract"
            label="Abstract"
            defaultValue={publication?.abstract}
            required
            multiline
            minRows={4}
            fullWidth
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <ComboBox
            name="type"
            label="Publication Type"
            inputLabel="Add publication type"
            possibleValues={publicationTypes || []}
            defaultValue={publication?.type}
            rules={{required: 'Publication type is required'}}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="dataTypes"
            label="Data Types"
            inputLabel="Add data type"
            possibleValues={dataTypes}
            defaultValues={publication?.dataTypes}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="topics"
            label="Topics"
            inputLabel="Add topic"
            possibleValues={topics}
            defaultValues={publication?.topics}
            rules={{required: 'At least one topic is required'}}
          />
        </Grid>

        <Grid size={{xs: 12, sm: 6}}>
          <MultiSelect
            name="keywords"
            label="Keywords"
            inputLabel="Add keyword"
            possibleValues={keywords}
            defaultValues={publication?.keywords}
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <InputList
            offset={relatedLinkOffset}
            list={relatedLinks}
            component={<LinkGroup rel="related" />}
            label="Link"
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <Accordion
            variant="outlined"
            expanded={showGeometry}
            onChange={handleGeometryAccordionToggle}
          >
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <FormLabel>Publication Study Area</FormLabel>
            </AccordionSummary>
            <AccordionDetails sx={{height: 350, paddingTop: 0}}>
              <GeometryInput
                name="geometry"
                defaultValue={publication?.geometry}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid size={{xs: 12}}>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <input
              style={{display: 'none'}}
              id="upload-button"
              type="file"
              accept="application/pdf"
              disabled={!!file}
              onChange={handleFileChange}
            />
            <label htmlFor="upload-button">
              <Button variant="outlined" component="span">
                {attachmentStatus}
              </Button>
            </label>
          </Box>
        </Grid>
      </Grid>

      {attachmentLink &&
        Object.keys(attachmentLink).map(key => (
          <TextField
            key={key}
            name={`links.1.${key}`}
            label="hidden"
            type="hidden"
            sx={{display: 'none'}}
            defaultValue={attachmentLink[key]}
          />
        ))}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 2,
          marginBottom: 5,
        }}
      >
        {canChangePublicationStatus(permissions) && (
          <Toggle
            label="Published"
            name="published"
            defaultChecked={publication?.published}
          />
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={uploadingAttachment}
        >
          Save Publication
        </Button>
      </Box>
    </Form>
  );
}

PublicationForm.propTypes = {
  onSubmit: func.isRequired,
  publication: object,
};

export default PublicationForm;
